<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <!-- Card Component -->
        <div class="card">
          <!-- Header -->
          <div class="card-header">
            <h4 class="card-title">Edit Accessory</h4>
          </div>

          <!-- Body -->
          <div class="card-body">
            <!-- Add accessory form starts -->
            <form @submit.prevent="editAccessory" enctype="multipart/form-data">
              <div class="row">
                <div class="col-md-6 col-xl-6 form-group">
                  <label class="control-label">Title <span class="required">*</span></label>
                  <input type="text" v-model="accessory.title" :class="{ 'is-invalid': Boolean(errors.title) }" minlength="3" maxlength="200" class="form-control">
                  <div v-for="(val, index) in errors.title" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                </div>

                <div class="col-md-6 col-xl-6 form-group">
                  <label class="control-label">Price <span class="required">*</span></label>
                  <input type="text" v-model="accessory.price" :class="{ 'is-invalid': Boolean(errors.price) }" minlength="3" maxlength="200" @keypress="decimalsOnly" class="form-control">
                  <div v-for="(val, index) in errors.price" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                </div>

                <div class="col-md-6 form-group">
                  <label class="control-label">User <span class="required">*</span></label>
                  <div class="search-select">
                    <span class="search-close" v-if="visible" @click="visible=false, keyword=accessory.user_name + ' | ' + accessory.country_code + ' ' + accessory.user_phone">
                      <i class="dripicons dripicons-cross"></i>
                    </span>
                    <input type="text" v-model="keyword" @keyup="fetchUsers()" :class="{ 'is-invalid': Boolean(errors.user_id) }" autocomplete="off" placeholder="Search here..." class="form-control">
                    
                    <select v-if="visible && Boolean(users.length)" v-model="accessory.added_user" :size="users.length < 10 ? (users.length <= 1 ? 2 : users.length) : 10" @change="setUser" class="form-control">
                      <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }} | {{ user.countrycode }} {{ user.phone_number }}</option>
                    </select>

                    <select v-if="visible && !Boolean(users.length)" v-model="accessory.added_user" size="2" class="form-control">
                      <option :value="null" disabled>No data results...</option>
                    </select>
                  </div>
                  <div v-for="(val, index) in errors.user_id" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                </div>

                <div class="col-md-6 form-group">
                  <label class="control-label">Category <span class="required">*</span></label>
                  <select v-model="accessory.cat_id" :class="{ 'is-invalid': Boolean(errors.cat_id) }" class="form-control">
                    <option :value="null" disabled>-- Select Category --</option>
                    <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.cat_name }}</option>
                  </select>
                  <div v-for="(val, index) in errors.cat_id" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                </div>

                <div class="col-md-12 col-xl-12 form-group">
                  <label class="control-label">Description <span class="required">*</span></label>
                  <textarea rows="3" max-rows="6" v-model="accessory.description" :class="{ 'is-invalid': Boolean(errors.description) }" class="form-control"></textarea>
                  <div v-for="(val, index) in errors.description" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                </div>

                <div class="col-md-12 col-xl-12 form-group">
                  <label class="control-label">Location <span class="required">*</span></label>
                  <GmapAutocomplete @place_changed="setPlace" class="form-control" @keypress.enter="$event.preventDefault()" placeholder="Search here..." :value="accessory.location" @change="setLocation" :class="{ 'is-invalid': Boolean(errors.location) }" />
                  <div v-for="(val, index) in errors.location" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                  <GmapMap v-if="accessory.location"
                    :center="center"
                    :zoom="18"
                    map-style-id="roadmap"
                    :options="mapOptions"
                    style="width: 100%; height: 50vmin"
                    ref="mapRef"
                    @click="handleMapClick">
                      <GmapMarker
                        :position="marker.position"
                        :clickable="true"
                        :draggable="true"
                        @drag="handleMarkerDrag"
                        @click="panToMarker"
                      />
                  </GmapMap>
                </div>

                <div class="col-md-6 col-xl-6 form-group">
                  <label class="control-label">Latitude <span class="required">*</span></label>
                  <input type="text" v-model="marker.position.lat" :class="{ 'is-invalid': Boolean(errors.latitude) }" @keypress="decimalsOnly" class="form-control">
                  <div v-for="(val, index) in errors.latitude" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                </div>

                <div class="col-md-6 col-xl-6 form-group">
                  <label class="control-label">Longitude <span class="required">*</span></label>
                  <input type="text" v-model="marker.position.lng" :class="{ 'is-invalid': Boolean(errors.longitude) }" @keypress="decimalsOnly" class="form-control">
                  <div v-for="(val, index) in errors.longitude" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                </div>

                <div class="col-md-12">
                  <div class="input-dynamic" :class="{ 'is-invalid': Boolean(errors.images) }">
                    <label class="control-label">Images <span class="required">*</span></label>
                    <div class="row">
                      <div class="col-md-12 mb-3" v-for="(img, index) in accessory.images" :key="img.id">
                        <div class="delete-image-wrapper">
                          <img :src="$image_url + img.image">
                          <span class="delete-image" @click="deleteImage(img.id, index)"><i class="fa fa-minus-circle text-danger"></i></span>
                        </div>
                      </div>
                    </div>
                    <div class="row" v-for="(input, index) in inputs" :key="index">
                      <div class="col-md-6 form-group">
                        <input type="file" accept=".jpg, .png, .jpeg" class="form-control" @change="setFile($event, index)">
                        <span>
                          <i class="fa fa-minus-circle text-danger" @click="remove(index)" v-show="index || (!index && inputs.length > 1)"></i>
                          <i class="fa fa-plus-circle text-green" @click="add()" v-show="index == inputs.length - 1"></i>
                        </span>
                      </div>
                      <div class="col-md-6">
                        <img v-if="input.preview_image" class="image-preview" :src="input.preview_image">
                      </div>
                    </div>
                  </div>
                  <div v-for="(val, index) in errors.images" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                </div>

                <div class="col-md-12 mt-3">
                  <label class="control-label">Video</label>
                  <input type="file" accept=".mp4, .ogx, .oga, .ogv, .ogg, .webm" @change="setVideo()" :class="{ 'is-invalid': Boolean(errors.video) }" class="form-control">
                  <div class="info-message">Leave this field empty if you don't want to add/edit the video</div>
                  <div v-for="(val, index) in errors.video" :key="index" class="invalid-feedback">
                    <p class="mb-0">{{ val }}</p>
                  </div>
                </div>

                <div class="col-md-12 text-end">
                  <button type="submit" class="btn btn-primary">Update</button>
				  <router-link :to="{ name: 'existingaccessories' }" class="btn btn-light">Cancel</router-link>
                </div>
              </div>
            </form>
            <!-- Add accessory form ends -->
          </div>
        </div>
        <!-- Card Component Ends -->
      </div>
    </div>
  </div>
</template>


<script>
	export default {
		name: "EditAccessory",
		

		data() {
			
			return {
				accessory: {
					title: '',					
					cat_id: null,
					added_user: null,
					user_name: '',
					user_phone: '',
					user_email: '',
					description: '',
					price: null,
					location: '',
					latitude: null,
					longitude: null,
					video: '',					
				},
				
				marker: { position: { lat: 10, lng: 10 } },
			   center: { lat: 10, lng: 10 },
			   current_place: null,

			   mapOptions: {
			      disableDefaultUI: true,
			   },

				users: {},
				categories: {},				
				inputs: [
	            {value: null, preview_image: null}
	         ],
	         images: [],
	         image_id: [],				
				keyword: '',
				visible: false,
				token: "",
				errors: {},
			};
		},
		mounted() {
			
		},

		created() {
			this.fetchAccessory();
			this.fetchCategories();			
			//this.geolocate();

		},

		methods: {			

			add() {
	         this.inputs.push({ value: null });
	      },

	      remove(index) {
	         this.inputs.splice(index, 1);
	         this.images.splice(index, 1);
	      }, 

	      setFile(e, index) {
	      	this.images.push(e.target.files[0]);
	      	this.inputs[index].image = e.target.files[0];
				this.inputs[index].preview_image = URL.createObjectURL(e.target.files[0]);
			},

			deleteImage(id, index) {
				this.$swal({
					icon: 'warning',
					title:"Confirm",
					text:'Are you sure you want to delete this image?',
					showCancelButton: true,
					confirmButtonText: 'Yes',
					confirmButtonColor: '#e64942',
					cancelButtonText: 'No',
					position: "center",
					showConfirmButton: true,
					timer: '',
					toast: false,
					cancelButtonColor: '#efefef',
				}).then((result) => {
					if (result.isConfirmed) {
						this.accessory.images.splice(index, 1);
						this.image_id.push(id);
						this.$swal('Deleted!', 'Your image has been deleted.', 'success');
					} else {
						
					}
				}).catch(err => {
					console.log(err);
				});
			},

			geolocate() {
		      navigator.geolocation.getCurrentPosition((position) => {
		         this.marker.position = {
		           lat: position.coords.latitude,
		           lng: position.coords.longitude,
		         };
		         this.panToMarker();
      		});
    		},

		   //sets the position of marker when dragged
		   handleMarkerDrag(e) {
		      this.marker.position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
		   },

		   //Moves the map view port to marker
		   panToMarker() {
		      this.$refs.mapRef.panTo(this.marker.position);
		      this.$refs.mapRef.setZoom(18);
		   },

		   //Moves the marker to click position on the map
		   handleMapClick(e) {
		      this.marker.position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
		      console.log(e);
		   },		 

		 	setPlace(place) {
		    	this.current_place = place;
		    	if(this.current_place) {
		    		const marker = {
			    		lat: this.current_place.geometry.location.lat(),
			    		lng: this.current_place.geometry.location.lng(),
			    	};
			    	this.center = marker;
			    	this.marker.position = marker;
			    	this.accessory.location = place.formatted_address;
		    	}
		   },

		   setLocation(e) {
		   	//alert(e.target.value);
		   	this.accessory.location = e.target.value;
		   },

			decimalsOnly(event) {
		      let keyCode = event.keyCode;
		      if (keyCode < 48 || keyCode > 57) {
		        event.preventDefault();
		      }
		    },
		    alphaSpacesOnly(event) {
		    	let keyCode = event.keyCode;
			    if ((keyCode >= 65 && keyCode <= 90) || 
			    	(keyCode >= 97 && keyCode <= 122) ||			    	
			    	(keyCode == 32)) {
			        return;
			    } else {
			    	event.preventDefault();
			    }
		    },
		   
			getToken() {
                if (localStorage.getItem('slo_admin')) {
                    var slo_admin =  JSON.parse(localStorage.getItem('slo_admin')); 
                    
                    if(slo_admin.token) {

                        this.token = slo_admin.token;
                        this.user = slo_admin;

                    } else {
                        this.$swal("", "Failed to fetch token, please login again", "error");
                        this.$router.push({ name: "Login" });
                    }

                } else {
                    this.$swal("", "Session expired, please login again", "error");
                    this.$router.push({ name: "Login" });
                }
            },

			fetchAccessory() {
				let url = '/api/admin/accessories/show?accessory_id=' + this.$route.params.id ;
            
	         this.getToken();
	         this.$axios
	            .get(url, {headers: {Authorization: 'Bearer ' + this.token,}})
	            .then(res => {
	               var responseData = res.data.data;               
	               this.accessory = responseData; 
	               
	               this.marker.position.lat = parseFloat(this.accessory.latitude)
	               this.marker.position.lng = parseFloat(this.accessory.longitude);

	               this.center.lat = parseFloat(this.accessory.latitude);
	               this.center.lng = parseFloat(this.accessory.longitude);

	               this.keyword = this.accessory.user_name + ' | ' + this.accessory.country_code + ' ' + this.accessory.user_phone;	                           
	            })
	            .catch(err => console.log(err));
			},

			fetchCategories() {
				let url = "/api/admin/accessories/category";			
				this.getToken();
				this.$axios
					.get(url, { headers: { Authorization: "Bearer " + this.token } })
					.then((res) => {
						var responseData = res.data;
						this.categories = responseData.data;
					})
					.catch((err) => console.log(err));
			},			

			fetchUsers() {
				let url = "/api/admin/users?limit=0&keyword=" + this.keyword;
				this.getToken();
				this.$axios
					.get(url, {
						headers: { Authorization: "Bearer " + this.token },
					})
					.then((res) => {
						var responseData = res.data.data;
						this.users = responseData.data;
						//console.log(this.users);
						this.visible = true;
						this.accessory.user_id = null;
					})
					.catch((err) => console.log(err));
			},
			

			setUser() {
				//alert(this.accessory.user_id.text);
				this.keyword = event.target.options[event.target.options.selectedIndex].label;
				//this.keyword = this.accessory.user_id;
				this.visible = false;
			},

			setVideo(event) {
				this.accessory.video = event.target.files[0];
			},

			editAccessory() {

				this.getToken();
				let formData = new FormData();
				formData.append("accessory_id", this.accessory.id);
				formData.append("title", this.accessory.title);
				formData.append("price", this.accessory.price);
				formData.append("cat_id", this.accessory.cat_id);
				formData.append("user_id", this.accessory.added_user);				
				formData.append("description", this.accessory.description);
				formData.append("location", this.accessory.location);
				formData.append("latitude", this.marker.position.lat);
				formData.append("longitude", this.marker.position.lng);
				formData.append("video", this.accessory.video);	

				if(this.images) {
					for(var j = 0; j < this.images.length; j++) {
					  	let file = this.images[j];
					   formData.append('images[' + j + ']', file);
					}
				}				

				formData.append('image_id', JSON.stringify(this.image_id));

				const config = {
					headers: {
						"content-type": "multipart/form-data",
						Authorization: "Bearer " + this.token,
					},
				};

				let url;
				url = "/api/admin/accessories/edit";

				this.$axios
					.post(url, formData, config)
					.then((response) => {
						if (response.data.status) {
							this.$swal("", response.data.message, "success");
						} else {
							this.$swal("", response.data.message, "error");
						}
						this.fetchAccessory();
						this.fetchCategories();		
						this.inputs = [{value: null, preview_image: null}];
	         		this.images = [];
	         		this.image_id = [];	
	         		this.errors = {};
					})
					.catch((error) => {
						this.errors = {};
						if (error.response) {
							if (error.response.data.message) {
								console.log(error.response.data);
								this.errors = error.response.data.message;
								console.log(this.errors);
								//this.$vToastify.error(error.response.message);
							} else {
								this.$swal("", "Something went wrong", "error");
							}
						} else {
							this.$swal("", "Something went wrong", "error");
						}
					});
			},
			
		},
	};
</script>
